import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  ArrowLeftIcon,
  Divider,
  Stepper,
  StatusBadge,
  AccordionItem,
  Accordion,
  SearchIcon,
  Table,
  TableHeader,
  TableHeaderData,
  TableContent,
  TableRow,
  TableBody,
  TableCell,
  TextOverflow,
  Checkbox,
  OpenInNewIcon,
  Modal,
  Link,
  Tooltip,
} from "@forge/common";
import {
  Card,
  CardHeader,
  CardContent,
  Label,
  Control,
  ControlError,
  ControlHelper,
  Input,
  DatePicker,
  Select,
  SelectOption,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  Button,
} from "@forge/common";
import { StepperStepStatus } from "@forge/common/dist/src/components/Stepper/StepperStep";
import ParticipantDetails from "./participant-details";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeftPanel from "../common/components/left-panel/left-panel";
import "./start-course-instance.scss";
import { COURSE_INSTANCE_CONSTANTS } from "./utils/constants";
import { FormErrors, FormData, TouchedFields, VMTable } from "./model";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../common/store/combinedReducer";
import Header from "../common/components/header/header";
import { CREATE_COURSE_CONSTANTS } from "../create-course/utils/constants";
import { VIEW_COURSE_CONSTANTS } from "../view-course/utils/constants";

import { setSelectedCard } from "../view-course-templates/store/view-course-template.action";
import { fetchGeneralDetailsRequest } from "../view-deployed-course-instance/store/vdc-instance.action";
import { getCourseDataById } from "../create-course/store/create-course.action";
import VmName from "../view-course/vm-name";
import { hideModel, showModel, showToast } from "../store/model/model.action";
import WarningPopup from "../view-course/popup/warning";
import config from "../../../../common/authentication/config";
import { getWMKSDetails, clearWMKSDetails } from "../../../store/action";
import { LIBRARY_ROUTES } from "../create-course/utils/constants";
import { getEmailTemplates } from "./store/start-course-instance.action";

const initialSteps = [
  {
    id: "in-progress",
    status: "incomplete" as StepperStepStatus,
    title: "General details",
    description: "Instance Details, Batch Size & Description",
  },
  {
    id: "completed",
    status: undefined,
    title: "Participants",
    description: "Invite Students, Assign Instructors",
  },
];

export default function StartCourseInstance() {
  const truncateWithTooltip = (text: string, maxLength: number = 50) => {
    if (!text) return text;

    if (text.length <= maxLength) {
      return text;
    }

    const truncated = text.substring(0, maxLength) + "...";
    return (
      <Tooltip content={text} position="bottom-left">
        <span>{truncated}</span>
      </Tooltip>
    );
  };

  const [courseName, setCourseName] = useState("");
  const [minDate, setMinDate] = useState(new Date());
  const [isDirty, setIsDirty] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = React.useState<boolean>(false);
  const [savedStudents, setSavedStudents] = useState<string[]>([]);
  const [savedInstructors, setSavedInstructors] = useState<string[]>([]);
  const [instanceId, setInstanceId] = useState<string | undefined>(undefined);
  const [filterType, setFilterType] = useState<string>("Name");
  const [filteredTableData, setFilteredTableData] = useState<VMTable[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tableData, setTableData] = useState<VMTable[]>([]);
  const [associatedVappTemplate, setAssociatedVappTemplate] =
    useState<string>("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] =
    useState<boolean>(false);
  const [isCancelButtonTriggered, setIsCancelButtonTriggered] =
    useState<boolean>(false);

  const { instanceId: urlInstanceId } =
    useParams<{ instanceId?: string }>() || {};

  const [touchedFields, setTouchedFields] = useState<TouchedFields>({
    instancename: false,
    description: false,
    batchSize: false,
    startDate: false,
    courseSelect: false,
    courseDuration: false,
    courseType: false,
    expiryDate: false,
    expiryMailTemplateId: false,
    deleteMailTemplateId: false,
    sendDeployMail: false,
    deployMailTemplateId: false,
  });

  const [showGeneralDetails, setShowGeneralDetails] = useState(true);

  const [vmTableHeaders, setVmTableHeaders] = useState<TableHeaderData[]>([
    {
      id: "vmName",
      content: "NAME",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: true,
      canResize: true,
    },
    {
      id: "osType",
      content: "OS TYPE",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: true,
      canResize: true,
    },
    {
      id: "osVersion",
      content: "OS VERSION",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: true,
      canResize: true,
    },
    {
      id: "ram",
      content: "RAM",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: false,
      canResize: true,
    },
    {
      id: "cpu",
      content: "CPU",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: false,
      canResize: true,
    },
    {
      id: "ipAddress",
      content: "IP ADDRESS",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: false,
      canResize: true,
    },
    {
      id: "storage",
      content: "STORAGE",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: false,
      canResize: true,
    },
    {
      id: "vmPausable",
      content: "VM PAUSABLE",
      className: "vpp-table-border table-header-cell !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px]",
      canSort: false,
      canResize: false,
    },
  ]);

  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | undefined>(undefined);
  const [displayRows, setDisplayRows] = useState<VMTable[]>([]);

  const columnValueGetters: Record<string, (row: VMTable) => string | number> = {
    vmName: (row) => row.vmName ?? "",
    osType: (row) => row.osType ?? "",
    osVersion: (row) => row.osVersionName ?? "",
    ram: (row) => row.ramValue ?? "",
    cpu: (row) => row.cpu ?? "",
    ipAddress: (row) => row.iPv4 ?? "",
    storage: (row) => row.storage ?? "",
    vmPausable: (row) => row.isPausable ?? "",
  };

  const sortTableData = (
    rows: VMTable[],
    field?: string,
    direction?: "asc" | "desc"
  ): VMTable[] => {
    if (!field || !direction) return rows;
    const getter = columnValueGetters[field];
    if (!getter) return rows;

    return [...rows].sort((a, b) => {
      let valueA = getter(a);
      let valueB = getter(b);

      if (field === "ram" || field === "cpu" || field === "storage") {
        const numA = parseInt(String(valueA).replace(/[^0-9]/g, "")) || 0;
        const numB = parseInt(String(valueB).replace(/[^0-9]/g, "")) || 0;
        return direction === "asc" ? numA - numB : numB - numA;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return direction === "asc" ? valueA - valueB : valueB - valueA;
      }

      const aStr = String(valueA || "").toLowerCase();
      const bStr = String(valueB || "").toLowerCase();
      return direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  };

  const handleTableSort = (
    direction: "asc" | "desc" | undefined,
    id: string
  ) => {
    setSortField(direction ? id : undefined);
    setSortDirection(direction);
    setVmTableHeaders((prev) =>
      prev.map((header) => ({
        ...header,
        sort: header.id === id ? direction : undefined,
      }))
    );
    setDisplayRows(sortTableData(filteredTableData, direction ? id : undefined, direction));
  };

  const handleClickHam = () => {
    setShowLeftPanel((prev) => !prev);
  };

  const [errors, setErrors] = useState<FormErrors>({});
  const [generalDetailsStatus, setGeneralDetailsStatus] = useState<{
    id: string;
    status: StepperStepStatus | undefined;
  }>({ id: "in-progress", status: "incomplete" });
  const [participantsStatus, setParticipantsStatus] = useState<{
    id: string;
    status: StepperStepStatus | undefined;
  }>({ id: "completed", status: undefined });

  const steps = [
    {
      ...initialSteps[0],
      id: generalDetailsStatus.id,
      status: generalDetailsStatus.status,
    },
    {
      ...initialSteps[1],
      id: participantsStatus.id,
      status: participantsStatus.status,
    },
  ];

  const location = useLocation();
  const { courseId } = location.state || {};

  const [formData, setFormData] = useState<FormData>({
    instancename: "",
    description: "",
    batchSize: "",
    startDate: "",
    courseSelect: "",
    courseDuration: "",
    courseType: "instructorLed",
    expiryDate: "",
    deployMailTemplateId: "",
    expiryMailTemplateId: "",
    sendDeployMail: false,
    deleteMailTemplateId: "",
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openVm, setOpenVm] = React.useState(false);
  const [vmWares, setVmWares] = React.useState<any>({});
  const [courseInstanceDetails, setcourseInstanceDetails] = useState<[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<any>();

  const { generalDetails } = useSelector((state: AppState) => {
    return state.viewDeployedCourseInstanceReducer;
  });

  const { viewCourseViewData } = useSelector((state: AppState) => {
    return state.viewCourseTemplateReducer;
  });

  const courseDefData = useSelector((state: AppState) => {
    return state.createCourseReducer.courseDefinitionData.courseDefData;
  });

  let openedTabs: any;
  const wmksDetails = useSelector((state: AppState) => {
    return state.reducer.wmksDetails;
  });
  const wmksStatus = useSelector((state: AppState) => {
    return state.reducer.wmksStatus;
  });

  const emailTemplates = useSelector((state: AppState) => {
    return state.courseInstanceReducer.emailTemplates.templates;
  });

  useEffect(() => {
    if (wmksStatus === 409 && wmksDetails?.message) {
      dispatch(
        showToast({
          type: "error",
          title: "",
          subtitle: wmksDetails.message,
        })
      );
      setOpenVm(false);
      dispatch(clearWMKSDetails());
      return;
    }
    if (
      wmksDetails?.host !== null &&
      wmksDetails?.host !== undefined &&
      openVm &&
      wmksStatus === 200
    ) {
      if (vmWares?.vmName) {
        window
          .open(
            `/dashboard/vmware/${generalDetails?.siteId}/${vmWares?.vmName}`,
            "",
            "noopener"
          )
          ?.focus();
        localStorage.setItem(
          vmWares?.vmName,
          JSON.stringify({
            name: vmWares?.vmName,
            id: vmWares?.vmId,
            orgId: config?.ACADEMY_TENANT_ID,
            orgName: generalDetails?.courseInstanceName,
            tenantId: config?.ACADEMY_TENANT_ID,
            siteId: generalDetails?.siteId,
            wmksData: wmksDetails,
            isVmWindow: true,
          })
        );
        // openedTabs = Number(localStorage.getItem("openedTabs") || "0");
        // openedTabs += 1;
        // localStorage.setItem("openedTabs", openedTabs?.toString());
        setOpenVm(false);
      }
    }
  }, [wmksDetails, wmksStatus]);

  const updateSteps = useCallback(
    (stepIndex: number, id: string, status: string) => {
      if (stepIndex === 0) {
        setGeneralDetailsStatus({ id, status: status as StepperStepStatus });
      } else if (stepIndex === 1) {
        setParticipantsStatus({ id, status: status as StepperStepStatus });
      }
    },
    [setGeneralDetailsStatus, setParticipantsStatus]
  );

  useEffect(() => {
    setMinDate(new Date());
  }, []);

  // Helper function to detect HTML tags and script tags
  const containsHTMLOrScript = (value: string): boolean => {
    // Check for HTML tags (any content between < and >)
    const htmlTagRegex = /<[^>]*>/gi;
    // Check for script content (more comprehensive)
    const scriptRegex = /<script[\s\S]*?<\/script>/gi;
    const scriptAttributeRegex = /\s*on\w+\s*=/gi; // Event handlers like onclick, onload, etc.
    const javascriptProtocolRegex = /javascript:/gi;

    return (
      htmlTagRegex.test(value) ||
      scriptRegex.test(value) ||
      scriptAttributeRegex.test(value) ||
      javascriptProtocolRegex.test(value)
    );
  };

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};

    if (!formData || formData === null) {
      return false;
    }

    if (!formData.instancename.trim()) {
      newErrors.instancename =
        COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME_ERROR_MESSAGE;
    } else if (containsHTMLOrScript(formData.instancename)) {
      newErrors.instancename =
        COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME_HTML_ERROR;
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.instancename)) {
      newErrors.instancename =
        COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME_INVALID_CHARACTERS;
    } else if (urlInstanceId && formData.instancename.length > 64) {
      newErrors.instancename =
        COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME_LENGTH_ERROR_EDIT;
    } else if (!urlInstanceId && formData.instancename.length > 30) {
      newErrors.instancename =
        COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME_LENGTH_ERROR_CREATE;
    }

    if (formData?.description!.trim()) {
      if (containsHTMLOrScript(formData?.description!)) {
        newErrors.description =
          COURSE_INSTANCE_CONSTANTS.DESCRIPTION_HTML_ERROR;
      } else if (!/^[a-zA-Z0-9\s,.\-_]+$/.test(formData?.description!)) {
        newErrors.description =
          COURSE_INSTANCE_CONSTANTS.DESCRIPTION_INVALID_CHARACTERS;
      } else if (formData?.description!.length > 250) {
        newErrors.description = COURSE_INSTANCE_CONSTANTS.DESCRIPTION_SIZE_INFO;
      }
    }

    if (!urlInstanceId) {
      if (!formData.batchSize.trim()) {
        newErrors.batchSize =
          COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_ERROR_MESSAGE;
      } else if (
        !new RegExp(COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_REGEX).test(
          formData.batchSize
        )
      ) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_INFO;
      } else if (Number(formData.batchSize) <= 0) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_MIN;
      } else if (Number(formData.batchSize) > 20) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_MAX;
      }
    } else if (formData.batchSize.trim()) {
      if (
        !new RegExp(COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_REGEX).test(
          formData.batchSize
        )
      ) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_INFO;
      } else if (Number(formData.batchSize) <= 0) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_MIN;
      } else if (Number(formData.batchSize) > 20) {
        newErrors.batchSize = COURSE_INSTANCE_CONSTANTS.BATCH_SIZE_MAX;
      }
    }

    if (!urlInstanceId) {
      if (!formData.startDate) {
        newErrors.startDate =
          COURSE_INSTANCE_CONSTANTS.START_DATE_ERROR_MESSAGE;
      } else {
        const selectedDate = new Date(formData.startDate);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        if (selectedDate < currentDate) {
          newErrors.startDate = COURSE_INSTANCE_CONSTANTS.EXPIRY_DATE_INFO;
        } else {
          const maxAllowedDate = new Date(currentDate);
          maxAllowedDate.setFullYear(currentDate.getFullYear() + 1);

          if (selectedDate > maxAllowedDate) {
            newErrors.startDate =
              COURSE_INSTANCE_CONSTANTS.START_DATE_MAX_ERROR;
          }
        }
      }
    }

    if (urlInstanceId) {
      if (!formData.expiryDate) {
        newErrors.expiryDate =
          COURSE_INSTANCE_CONSTANTS.EXPIRY_DATE_ERROR_MESSAGE;
      } else if (formData.startDate && formData.expiryDate) {
        const expiryDate = new Date(formData.expiryDate);

        if (generalDetails?.expiryDate) {
          const originalExpiryDate = new Date(generalDetails.expiryDate);

          // Check if selected date is before the original expiry date
          if (expiryDate < originalExpiryDate) {
            newErrors.expiryDate =
              COURSE_INSTANCE_CONSTANTS.EXPIRY_DATE_BEFORE_ORIGINAL;
          } else {
            // Check if selected date is more than 89 days after original expiry date
            const maxExpiryDate = new Date(originalExpiryDate);
            maxExpiryDate.setDate(originalExpiryDate.getDate() + 89);
            if (expiryDate > maxExpiryDate) {
              newErrors.expiryDate =
                COURSE_INSTANCE_CONSTANTS.EXPIRY_DATE_VALIDATION;
            }
          }
        }
      }
    }

    // Course Duration validation
    const courseDurationStr = (formData.courseDuration || "").toString();
    if (!courseDurationStr.trim()) {
      newErrors.courseDuration =
        COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.ERRORS.REQUIRED;
    } else if (!/^\d+$/.test(courseDurationStr.trim())) {
      newErrors.courseDuration =
        COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.ERRORS.NUMERIC;
    } else {
      const duration = Number(courseDurationStr);
      if (duration < 1) {
        newErrors.courseDuration =
          COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.ERRORS.MIN;
      } else if (duration > 30) {
        newErrors.courseDuration =
          COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.ERRORS.MAX;
      }
    }

    if (!formData.courseSelect) {
      newErrors.courseSelect =
        COURSE_INSTANCE_CONSTANTS.COURSE_NAME_ERROR_MESSAGE;
    }

    if (!formData.courseType) {
      newErrors.courseType =
        COURSE_INSTANCE_CONSTANTS.COURSE_TYPE_ERROR_MESSAGE;
    }

    if(!urlInstanceId){
      if (!formData.expiryMailTemplateId) {
        newErrors.expiryMailTemplateId = COURSE_INSTANCE_CONSTANTS.EXPIRY_MAIL_TEMPLATE_ID_ERROR_MESSAGE;
      }

      if (!formData.deleteMailTemplateId) {
        newErrors.deleteMailTemplateId = COURSE_INSTANCE_CONSTANTS.DELETE_COMPLETION_TEMPLATE_ERROR_MESSAGE;
      }

      if (formData.sendDeployMail && !formData.deployMailTemplateId) {
        newErrors.deployMailTemplateId = COURSE_INSTANCE_CONSTANTS.SUCCESSFUL_DEPLOYMENT_TEMPLATE_ERROR_MESSAGE;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, urlInstanceId, generalDetails]);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!hasFetchedRef.current && !urlInstanceId && courseId) {
      dispatch(getCourseDataById(courseId));
      hasFetchedRef.current = true;
    }
  }, [dispatch, urlInstanceId, courseId]);

  useEffect(() => {
    dispatch(
      getEmailTemplates(),
    );
  },[courseId]);

  useEffect(() => {
    if (courseDefData && !urlInstanceId) {
      setCourseName(courseDefData.courseName || "");
      setSelectedCourse(courseDefData);

      setFormData((prevData) => ({
        ...prevData,
        courseSelect: courseDefData.courseName || "",
        courseDuration: courseDefData.duration
          ? courseDefData.duration.toString()
          : "",
      }));
    }
  }, [courseDefData, urlInstanceId]);

  useEffect(() => {
    if (isDirty) {
      validateForm();
    }
  }, [isDirty, validateForm]);

  const hasErrors = Object.keys(errors).length > 0;

  const isViewMode = Boolean(urlInstanceId);

  useEffect(() => {
    if ((!hasErrors && isDirty) || (isViewMode && isDirty && !hasErrors)) {
      setGeneralDetailsStatus({ id: "accepted", status: "valid" });
    } else {
      setGeneralDetailsStatus({ id: "in-progress", status: "incomplete" });
    }
  }, [hasErrors, isDirty, showGeneralDetails, isViewMode]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [showGeneralDetails]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      setGeneralDetailsStatus({ id: "accepted", status: "valid" });
      setParticipantsStatus({ id: "in-progress", status: "incomplete" });
      setShowGeneralDetails(false);
    } else {
      setTouchedFields({
        instancename: true,
        description: true,
        batchSize: true,
        startDate: true,
        courseSelect: true,
        courseDuration: true,
        courseType: true,
        expiryDate: true,
        deployMailTemplateId: true,
        expiryMailTemplateId: true,
        sendDeployMail: true,
        deleteMailTemplateId: true,
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [id]: value }));
    setTouchedFields((prev) => ({ ...prev, [id]: true }));
    setIsDirty(true);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData((prevData) => ({ ...prevData, startDate: value }));
    setTouchedFields((prev) => ({ ...prev, startDate: true }));
    setIsDirty(true);
  };

  const handleExpiryDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData((prevData) => ({ ...prevData, expiryDate: value }));
    setTouchedFields((prev) => ({ ...prev, expiryDate: true }));
    setIsDirty(true);
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = e.target;
    setFormData((prevData) => ({ ...prevData, courseType: id }));
    setTouchedFields((prev) => ({ ...prev, courseType: true }));
    setIsDirty(true);
  };

  const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData((prevData) => ({ ...prevData, batchSize: value }));
    setTouchedFields((prev) => ({ ...prev, batchSize: true }));
    setIsDirty(true);
  };

  const getFieldValidationClass = (fieldName: keyof TouchedFields) => {
    if (touchedFields[fieldName] && errors[fieldName]) {
      return "input-field error-border text-sm";
    }
    return "input-field text-sm";
  };

  const saveStudents = (students: string[]) => {
    setSavedStudents(students);
  };

  const saveInstructors = (instructors: string[]) => {
    setSavedInstructors(instructors);
  };

  const getTemplateName = (templateId: string | number, templates: any[] = []) => {
    if (!templateId || !templates.length) return "";
    const template = templates.find((t: any) => String(t.templateId) === String(templateId));
    return template?.templateName || "";
  };

  useEffect(() => {
    if (generalDetails && urlInstanceId) {
      const formatDateToYYYYMMDD = (isoDate: string) => {
        if (!isoDate) return "";
        const date = new Date(isoDate);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      setFormData({
        instancename: generalDetails.courseInstanceName || "",
        description: generalDetails.courseInstanceDescription || "",
        batchSize: generalDetails.batchSize || "",
        startDate: formatDateToYYYYMMDD(generalDetails.startDate) || "",
        courseSelect: generalDetails.courseTemplateAssociated || "",
        courseDuration: generalDetails.courseDuration
          ? generalDetails.courseDuration.toString()
          : "",
        courseType: generalDetails.courseType ? "instructorLed" : "selfPaced",
        expiryDate: formatDateToYYYYMMDD(generalDetails.expiryDate) || "",
        deployMailTemplateId: generalDetails.deployMailTemplateId || "",
        expiryMailTemplateId: generalDetails.expiryMailTemplateId || "",
        sendDeployMail: generalDetails.sendDeployMail || false,
        deleteMailTemplateId: generalDetails.deleteMailTemplateId || "",
      });
      setSavedStudents(
        generalDetails.invitedStudent ? [generalDetails.invitedStudent] : []
      );
      setSavedInstructors(generalDetails.assignedInstructor || []);
      setcourseInstanceDetails(generalDetails);
      setAssociatedVappTemplate(generalDetails.associatedVappTemplate || "");
      setTableData(generalDetails.vmWares || []);
      setFilteredTableData(generalDetails.vmWares || []);
      setDisplayRows(generalDetails.vmWares || []);

      setIsDirty(true);
    }
  }, [generalDetails, courseInstanceDetails, urlInstanceId]);

  useEffect(() => {
    if (urlInstanceId) {
      setInstanceId(urlInstanceId);
      dispatch(fetchGeneralDetailsRequest(urlInstanceId));
    }
  }, [urlInstanceId, dispatch]);

  const handleSearchChange = (e: any) => {
    const query = e.target.value || "";
    setSearchQuery(query);
    filterData(filterType, query);
  };

  const handleFilterTypeChange = (e: any) => {
    const value = e?.target?.value;
    if (value) {
      setFilterType(value);
      setSearchQuery("");
      filterData(value, "");
    }
  };

  const filterData = (type: string, query: string) => {
    let filtered: VMTable[];
    if (!query || !query.trim()) {
      filtered = tableData;
    } else {
      const lowercasedQuery = query.trim().toLowerCase();
      switch (type) {
        case "Name":
          filtered = tableData.filter((item) =>
            item.vmName.toLowerCase().includes(lowercasedQuery)
          );
          break;
        case "OS Type":
          filtered = tableData.filter((item) =>
            item.osType.toLowerCase().includes(lowercasedQuery)
          );
          break;
        default:
          filtered = tableData;
      }
    }

    setFilteredTableData(filtered);
    setDisplayRows(sortTableData(filtered, sortField, sortDirection));
  };

  const handleContinueWithoutSaving = () => {
    setShowCancelConfirmation(false);
    if (isCancelButtonTriggered) {
      dispatch(
        showToast({
          type: "warning",
          title: instanceId
            ? COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST
              .UPDATION_CANCELLED
            : COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST
              .CREATION_CANCELLED,
          subtitle:
            COURSE_INSTANCE_CONSTANTS.CANCEL_WITHOUT_SAVING_TOAST.NO_DATA_SAVED,
        })
      );
    }

    setIsCancelButtonTriggered(false);
    dispatch(setSelectedCard(urlInstanceId ? 3 : 1));
    navigate("/academy/viewCourseTemplates");
  };

  const handleBackToLibraryManagement = () => {
    const pathSegment = window.location.pathname
      .split("/academy")[1]
      ?.split("/")[1];
    const highlightPaths = [
      LIBRARY_ROUTES.CREATE_COURSE,
      LIBRARY_ROUTES.COPY_COURSE,
      LIBRARY_ROUTES.EDIT_COURSE,
      LIBRARY_ROUTES.START_COURSE_INSTANCE,
      LIBRARY_ROUTES.EDIT_COURSE_INSTANCE,
    ];
    if (highlightPaths.includes(pathSegment)) {
      setIsCancelButtonTriggered(false);
      setShowCancelConfirmation(true);
    } else {
      dispatch(setSelectedCard(urlInstanceId ? 3 : 1));
      navigate("/academy/viewCourseTemplates");
    }
  };

  const getMksToken = async (data: any) => {
    openedTabs = Number(localStorage.getItem("openedTabs") || "0");
    if (openedTabs >= 10) {
      dispatch(
        showToast({
          type: "error",
          title: "",
          subtitle: CREATE_COURSE_CONSTANTS.MAX_VMS_OPENED,
        })
      );
    } else {
      dispatch(
        showModel({
          childTemplate: () => (
            <WarningPopup
              title={"!Warning!"}
              description={
                "Install antivirus software on the available nodes to ensure the optimum cyber security and protection."
              }
              btnText={VIEW_COURSE_CONSTANTS.CANCEL}
              btnText1={VIEW_COURSE_CONSTANTS.CONFIRM}
              onCancel={() => dispatch(hideModel())}
              onTrigger={() => {
                setVmWares({
                  vmId: `vm-${data?.vcdVmId}`,
                  vmName: data?.vmName,
                });
                dispatch(hideModel());
                dispatch(
                  getWMKSDetails({
                    id: `vm-${data?.vcdVmId}`,
                    orgId: config?.ACADEMY_TENANT_ID,
                    accessToken: localStorage.getItem("access_token"),
                    siteId: generalDetails?.siteId,
                    orgName: generalDetails?.courseInstanceName,
                    tenantId: config?.ACADEMY_TENANT_ID,
                  })
                );
                setOpenVm(true);
              }}
            />
          ),
        })
      );
    }
  };

  const handleCourseDurationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;
    setFormData((prevData) => ({ ...prevData, courseDuration: value }));
    setTouchedFields((prev) => ({ ...prev, courseDuration: true }));
    setIsDirty(true);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>, fieldName: string) => {
    const { value } = e.target;
    setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setIsDirty(true);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    //console.log("formData before update:", formData);
    setFormData((prevData) => ({ 
      ...prevData, 
      sendDeployMail: checked,
      deployMailTemplateId: prevData.deployMailTemplateId
    }));
    setTouchedFields((prev) => ({ ...prev, sendDeployMail: true}));
    setIsDirty(true);
  };

  return (
    <>
      <div className="h-full flex">
        <div className="h-full flex-shrink-0">
          <LeftPanel showLeftPanel={showLeftPanel} />
        </div>
        <div className="flex-1 min-w-0 text-white p-4">
          <div className="mt-3">
            <Header handleClickHam={handleClickHam} />
          </div>
          <div
            className="start-instance-wrapper flex flex-col"
            data-testid="startInstanceForm"
          >
            <div className="start-instance-header flex items-center flex-row justify-between">
              <h1 className="start-instance-title mb-0 font-bold !text-base xl-low:!text-base xl-mid:!text-[23px] xl-high:!text-[26px]">
                {instanceId ? (
                  <>
                    {COURSE_INSTANCE_CONSTANTS.EDIT_INSTANCE} -{" "}
                    {truncateWithTooltip(formData.instancename, 50)}
                  </>
                ) : (
                  <>
                    {COURSE_INSTANCE_CONSTANTS.START_INSTANCE} -{" "}
                    {truncateWithTooltip(courseName, 50)}
                  </>
                )}
              </h1>
              <button
                onClick={handleBackToLibraryManagement}
                className="back-button flex items-center cursor-pointer text-sm font-bold bg-transparent border-none text-inherit xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]"
                data-testid="back-to-library"
              >
                <ArrowLeftIcon className="xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]" />
                {COURSE_INSTANCE_CONSTANTS.BACK_TO_LIBRARY_MANAGEMENT}
              </button>
            </div>

            <div className="form-container-scrollable" ref={scrollContainerRef}>
              <div
                className="stepper-container dark:bg-container-dark"
                data-testid="stepper-container"
              >
                <Stepper
                  className="custom-stepper [&_[class*='StepperStep_title']]:text-base [&_[class*='StepperStep_title']]:xl-low:!text-sm [&_[class*='StepperStep_title']]:xl-mid:!text-[21px] [&_[class*='StepperStep_title']]:xl-high:!text-[24px] [&_[class*='stepper-step-title']]:text-base [&_[class*='stepper-step-title']]:xl-low:!text-sm [&_[class*='stepper-step-title']]:xl-mid:!text-[21px] [&_[class*='stepper-step-title']]:xl-high:!text-[24px] [&_div>h4>span]:text-base [&_div>h4>span]:xl-low:!text-sm [&_div>h4>span]:xl-mid:!text-[21px] [&_div>h4>span]:xl-high:!text-[24px] [&_[class*='StepperStep_description']]:text-sm [&_[class*='StepperStep_description']]:xl-low:!text-xs [&_[class*='StepperStep_description']]:xl-mid:!text-[18px] [&_[class*='StepperStep_description']]:xl-high:!text-[20px] [&_[class*='StepperStep_description']]:!leading-6 [&_[class*='stepper-step-description']]:text-sm [&_[class*='stepper-step-description']]:xl-low:!text-xs [&_[class*='stepper-step-description']]:xl-mid:!text-[18px] [&_[class*='stepper-step-description']]:xl-high:!text-[20px] [&_[class*='stepper-step-description']]:!leading-6 [&_div>p>span]:text-sm [&_div>p>span]:xl-low:!text-xs [&_div>p>span]:xl-mid:!text-[18px] [&_div>p>span]:xl-high:!text-[20px] [&_div>p>span]:!leading-6"
                  currentStep="in-progress"
                  data-testid="course-stepper"
                  onStepChange={function noRefCheck() { }}
                  orientation="horizontal"
                  steps={steps}
                />
              </div>
              {showGeneralDetails && (
                <form onSubmit={handleSubmit} className="w-full min-w-0">
                  <Card className="mx-auto w-full max-w-full min-w-0 custom-card">
                    <Accordion
                      className="w-full dark:bg-container-dark"
                      id="general-details-accordion"
                      multi={true}
                      data-testid="general-details-accordion"
                      defaultOpenIndex={[0]}
                    >
                      <AccordionItem
                        id="general-details-section"
                        data-testid="general-details-section"
                        title={
                          <span className="!text-base xl-low:!text-[16px] xl-mid:!text-[23px] xl-high:!text-[26px]">
                            {COURSE_INSTANCE_CONSTANTS.GENERAL_DETAILS}
                          </span>
                        }
                        className="text-base font-semibold"
                      >
                    <CardContent top="condensed">
                      <div className="grid grid-cols-[5fr_0.5fr_5fr] xl:grid-cols-[5fr_0.5fr_5fr] gap-1 w-full min-w-0 xl-low:!pt-0 xl-mid:!pt-4 xl-high:!pt-6">
                        <div className="grid grid-cols-1 gap-2">
                          <Control className="!pb-0">
                            <Label
                              required
                              className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!mb-4 xl-high:!text-[24px] xl-high:!mb-4"
                            >
                              {COURSE_INSTANCE_CONSTANTS.INSTANCE_NAME}
                            </Label>
                            <Input
                              className={`${getFieldValidationClass(
                                "instancename"
                              )} !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]`}
                              size="regular"
                              type="text"
                              placeholder="Instance name"
                              id="instancename"
                              data-testid="instancename"
                              value={formData.instancename}
                              onChange={handleChange}
                              disabled={isViewMode}
                              autoComplete="off"
                            />
                            {touchedFields.instancename &&
                              errors.instancename ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.instancename}
                              </ControlError>
                            ) : null}
                          </Control>

                          <Control>
                            <Label className="label !pt-4 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!mb-4 xl-high:!text-[24px] xl-high:!mb-4">
                              {COURSE_INSTANCE_CONSTANTS.INSTANCE_DESCRIPTION}
                            </Label>
                            <TextArea
                              size="regular"
                              className={`${getFieldValidationClass(
                                "description"
                              )} no-resize !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]`}
                              rows={2}
                              placeholder="Description"
                              id="description"
                              data-testid="description"
                              value={formData.description}
                              onChange={handleChange}
                              disabled={isViewMode}
                            />
                            <ControlHelper className="info-text !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">
                              Max 250 characters
                            </ControlHelper>
                            {touchedFields.description && errors.description ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.description}
                              </ControlError>
                            ) : null}
                          </Control>
                          {!urlInstanceId && (
                            <Control className="!pb-0">
                              <Label
                                required
                                className="label !mb-0 !text-sm xl-low:!text-[14px] !leading-[50px] xl-mid:!text-[21px] !leading-[50px] xl-high:!text-[24px] !leading-[50px]"
                              >
                                {COURSE_INSTANCE_CONSTANTS.BATCH_SIZE}
                              </Label>
                              <Input
                                size="regular"
                                className={`${getFieldValidationClass(
                                  "batchSize"
                                )} no-spinner !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]`}
                                placeholder="Batch Size"
                                id="batchSize"
                                data-testid="batchSize"
                                value={formData.batchSize}
                                onChange={handleBatchSizeChange}
                                disabled={isViewMode}
                                autoComplete="off"
                              />
                              {touchedFields.batchSize && errors.batchSize ? (
                                <ControlError
                                  showIcon={false}
                                  className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                >
                                  {errors.batchSize}
                                </ControlError>
                              ) : null}
                            </Control>
                          )}

                          <Control className="start-date !pt-4 !pb-0">
                            <Label
                              required
                              className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!mb-4 xl-high:!text-[24px] xl-high:!mb-4"
                            >
                              {COURSE_INSTANCE_CONSTANTS.START_DATE}
                            </Label>
                            <DatePicker
                              className={`${getFieldValidationClass(
                                "startDate"
                              )} !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px] ${isViewMode ? "cursor-not-allowed" : ""
                                }`}
                              calendarPosition="top-right"
                              placeholder
                              size="regular"
                              id="startDate"
                              data-testid="startDate"
                              minDate={
                                !urlInstanceId
                                  ? `${minDate.getFullYear()}-${minDate.getMonth() + 1
                                  }-${minDate.getDate()}`
                                  : undefined
                              }
                              value={formData.startDate}
                              onChange={handleDateChange}
                              disabled={isViewMode}
                            />
                            {touchedFields.startDate && errors.startDate ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.startDate}
                              </ControlError>
                            ) : null}
                          </Control>

                          {urlInstanceId && (
                            <Control className="expiry-date">
                              <Label
                                required
                                className="label !pt-4 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!mb-4 xl-high:!text-[24px] xl-high:!mb-4"
                              >
                                Expiry Date
                              </Label>
                              <DatePicker
                                className={`${getFieldValidationClass(
                                  "expiryDate"
                                )} !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]`}
                                calendarPosition="top-right"
                                placeholder
                                size="regular"
                                id="expiryDate"
                                data-testid="expiryDate"
                                minDate={
                                  generalDetails?.expiryDate
                                    ? (() => {
                                      const originalExpiry = new Date(
                                        generalDetails.expiryDate
                                      );
                                      return `${originalExpiry.getFullYear()}-${originalExpiry.getMonth() + 1
                                        }-${originalExpiry.getDate()}` as `${number}-${number}-${number}`;
                                    })()
                                    : formData.startDate
                                      ? (formData.startDate as `${number}-${number}-${number}`)
                                      : (`${minDate.getFullYear()}-${minDate.getMonth() + 1
                                        }-${minDate.getDate()}` as `${number}-${number}-${number}`)
                                }
                                maxDate={
                                  generalDetails?.expiryDate
                                    ? (() => {
                                      const originalExpiry = new Date(
                                        generalDetails.expiryDate
                                      );
                                      const max = new Date(originalExpiry);
                                      max.setDate(
                                        originalExpiry.getDate() + 89
                                      );
                                      return `${max.getFullYear()}-${max.getMonth() + 1
                                        }-${max.getDate()}`;
                                    })()
                                    : formData.startDate
                                      ? (() => {
                                        const start = new Date(
                                          formData.startDate
                                        );
                                        const max = new Date(start);
                                        max.setDate(start.getDate() + 89);
                                        return `${max.getFullYear()}-${max.getMonth() + 1
                                          }-${max.getDate()}`;
                                      })()
                                      : `${minDate.getFullYear()}-${minDate.getMonth() + 1
                                      }-${minDate.getDate()}`
                                }
                                value={formData.expiryDate}
                                onChange={handleExpiryDateChange}
                              />
                              {touchedFields.expiryDate && errors.expiryDate ? (
                                <ControlError
                                  showIcon={false}
                                  className="text-sm xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                >
                                  {errors.expiryDate}
                                </ControlError>
                              ) : null}
                            </Control>
                          )}
                        </div>

                        <div></div>

                        <div>
                          <div className="mb-0">
                            <Label className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                              {COURSE_INSTANCE_CONSTANTS.COURSE_CONFIGURATION}
                            </Label>
                          </div>
                          <Control className="!pb-0">
                            <Label className="label !pt-0 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!pt-[16px] xl-high:!text-[24px] xl-high:!pt-[20px]">
                              {COURSE_INSTANCE_CONSTANTS.COURSE_NAME}
                            </Label>
                            <Input
                              className={`${getFieldValidationClass(
                                "courseSelect"
                              )} !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px] cursor-not-allowed`}
                              size="regular"
                              type="text"
                              id="courseSelect"
                              data-testid="courseSelect"
                              value={formData.courseSelect}
                              disabled={true}
                              readOnly={true}
                              autoComplete="off"
                            />
                          </Control>

                          <Control>
                            <Label
                              required
                              className="label !pt-4 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!pt-[24px]  xl-high:!text-[24px] xl-high:!pt-[24px]"
                            >
                              {COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.LABEL}
                            </Label>
                            <Input
                              size="regular"
                              className={`${getFieldValidationClass(
                                "courseDuration"
                              )} no-spinner !mb-0 !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]`}
                              placeholder={
                                COURSE_INSTANCE_CONSTANTS.COURSE_DURATION
                                  .PLACEHOLDER
                              }
                              id="courseDuration"
                              data-testid="courseDuration"
                              value={formData.courseDuration}
                              onChange={handleCourseDurationChange}
                              disabled={isViewMode}
                              autoComplete="off"
                            />
                            {touchedFields.courseDuration &&
                              errors.courseDuration ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.courseDuration}
                              </ControlError>
                            ) : null}
                            <ControlHelper className="info-text !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">
                              {
                                COURSE_INSTANCE_CONSTANTS.COURSE_DURATION.ERRORS
                                  .NUMERICAL
                              }
                            </ControlHelper>
                          </Control>

                          <Control className="!pb-0">
                            <Label className="label pt-4 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!pt-[24px] xl-high:!text-[24px] xl-high:!pt-[24px]">
                              {COURSE_INSTANCE_CONSTANTS.COURSE_TYPE}
                            </Label>
                            <RadioButtonGroup
                              id="radio-form"
                              className="label xl-low:!pt-0 xl-mid:!pt-2 xl-high:!pt-4"
                            >
                              <RadioButton
                                id="instructorLed"
                                data-testid="instructorLed"
                                value="instructorLed"
                                checked={
                                  formData.courseType === "instructorLed"
                                }
                                onChange={handleRadioChange}
                                disabled={isViewMode}
                              >
                                <span className="radio-button !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]">
                                  Instructor Led
                                </span>
                              </RadioButton>
                              <RadioButton
                                id="selfPaced"
                                data-testid="selfPaced"
                                value="selfPaced"
                                checked={formData.courseType === "selfPaced"}
                                onChange={handleRadioChange}
                                disabled={isViewMode}
                              >
                                <span className="radio-button !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]">
                                  Self Paced
                                </span>
                              </RadioButton>
                            </RadioButtonGroup>
                            {touchedFields.courseType && errors.courseType ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.courseType}
                              </ControlError>
                            ) : null}
                          </Control>

                          {!urlInstanceId && (<><Control className="!pb-0 select-template-block">
                            <Label 
                              required
                              className="label pt-4 !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!pt-[24px] xl-high:!text-[24px] xl-high:!pt-[24px]"
                            >
                              {COURSE_INSTANCE_CONSTANTS.ENABLE_DISABLE_STUDENT_EMAIL}
                            </Label>
                            <Select
                              height={1}
                              className="text-sm select-field font-normal font-w-400 !border-none !outline-none xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              id="deletionAlertTemplate"
                              data-testid="deletionAlertTemplate"
                              onChange={(e) => handleSelectChange(e as any, "expiryMailTemplateId")}
                              placeholder="Deletion Alert Template"
                              defaultValue={getTemplateName(formData.expiryMailTemplateId, emailTemplates?.expiryTemplates)}
                              size="regular"
                              disabled={isViewMode}
                            >
                              {emailTemplates?.expiryTemplates?.map((template:any) => (
                                <SelectOption
                                  key={template.templateId}
                                  label={template.templateName}
                                  value={template.templateId}
                                  className="text-sm font-normal xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                />
                              ))}
                              </Select>
                            {touchedFields.expiryMailTemplateId && errors.expiryMailTemplateId ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.expiryMailTemplateId}
                              </ControlError>
                            ) : null}
                          </Control>

                          <Control className="!pb-0 pt-3 select-template-block template-block">
                            <Select
                              height={1}
                              className="text-sm select-field font-normal font-w-400 !border-none !outline-none xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              id="deleteCompletionTemplate"
                              data-testid="deleteCompletionTemplate"
                              onChange={(e) => handleSelectChange(e as any, "deleteMailTemplateId")}
                              placeholder="Deletion Completion Template"
                              defaultValue={getTemplateName(formData.deleteMailTemplateId, emailTemplates?.deleteTemplates)}
                              size="regular"
                              disabled={isViewMode}
                            >
                              {emailTemplates?.deleteTemplates?.map((template: any) => (
                                <SelectOption
                                  key={template.templateId}
                                  label={template.templateName}
                                  value={template.templateId}
                                  className="text-sm font-normal xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                />
                              ))}
                              </Select>
                            {touchedFields.deleteMailTemplateId && errors.deleteMailTemplateId ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.deleteMailTemplateId}
                              </ControlError>
                            ) : null}
                          </Control>

                          <Control className="!pb-0 pt-3 template-block">
                            <div className="flex items-center">
                              <Checkbox
                                className="cursor-pointer enable-deployment-checkbox"
                                id="enableDeploymentTemplate"
                                data-testid="enableDeploymentTemplate"
                                checked={formData.sendDeployMail}
                                onChange={handleCheckboxChange}
                                disabled={isViewMode}
                              />
                              <div className="w-full pl-3 select-withoutControl">
                                <Select
                                  height={1}
                                  className="text-sm select-field font-normal font-w-400 !border-none !outline-none xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                  id="successfullyDeploymentTemplate"
                                  data-testid="successfullyDeploymentTemplate"
                                  onChange={(e) => handleSelectChange(e as any, "deployMailTemplateId")}
                                  placeholder="Successful Deployment Template"
                                  defaultValue={getTemplateName(formData.deployMailTemplateId, emailTemplates?.deployTemplates)}
                                  size="regular"
                                  disabled={!formData.sendDeployMail || isViewMode}
                                >
                                  {emailTemplates?.deployTemplates?.map((template: any) => (
                                    <SelectOption
                                      key={template.templateId}
                                      label={template.templateName}
                                      value={template.templateId}
                                      className="text-sm font-normal xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                    />
                                  ))}
                                </Select>
                              </div>
                            </div>
                            {touchedFields.sendDeployMail && errors.sendDeployMail && (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.sendDeployMail}
                              </ControlError>
                            )}
                            {touchedFields.deployMailTemplateId && errors.deployMailTemplateId ? (
                              <ControlError
                                showIcon={false}
                                className="text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                              >
                                {errors.deployMailTemplateId}
                              </ControlError>
                            ) : null}
                          </Control></> )}

                          {urlInstanceId && (
                            <div className="!pt-4">
                              <Label className="label !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-mid:!pt-[16px] xl-high:!text-[24px] xl-high:!pt-[20px]">
                                {COURSE_INSTANCE_CONSTANTS.EMAIL_STATUS}
                              </Label>
                              <div className="text-white flex items-center">
                                {generalDetails?.isEmailSent && (
                                  <>
                                    <StatusBadge
                                      color="green"
                                      className="mr-2 !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]"
                                      data-testid="email-status-success"
                                    />
                                    <span
                                      className="!text-[14px]"
                                      data-testid="email-status-text-success"
                                    >
                                      Success
                                    </span>
                                  </>
                                )}
                                {!generalDetails?.isEmailSent && (
                                  <>
                                    <StatusBadge
                                      color="red"
                                      className="mr-2 !text-sm xl-low:!text-[14px] xl-mid:!text-[20px] xl-high:!text-[22px]"
                                      data-testid="email-status-pending"
                                    />
                                    <span
                                      className="!text-[14px]"
                                      data-testid="email-status-text-pending"
                                    >
                                      Pending
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                    </AccordionItem>
                    </Accordion>
                    {urlInstanceId && (
                      <Accordion
                        className="mx-auto w-full max-w-full min-w-0 dark:bg-container-dark pb-[100px] rounded-lg overflow-hidden"
                        id="accordion"
                        multi={true}
                        data-testid="accordion"
                        defaultOpenIndex={[0, 1]}
                      >
                        <AccordionItem
                          id="vpp-template-details"
                          data-testid="vpp-template-details"
                          title={
                            <div className="flex items-center">
                              <span className="text-base xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                                vApp Details
                              </span>
                            </div>
                          }
                          className="text-base font-semibold"
                        >
                          <div className="vapp-details-header p-[10px]">
                            <div>
                              <span className="text-sm text-color-tertiary-dark mr-2 xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px]">
                                {VIEW_COURSE_CONSTANTS.associatedVappTemplate}
                              </span>{" "}
                              <span className="text-sm xl-low:!text-[12px] xl-mid:!text-[20px] xl-high:!text-[22px]">
                                {associatedVappTemplate}
                              </span>
                            </div>
                          </div>
                          <div className="overflow-hidden w-full min-w-0">
                            <Card className="border-unset mt-4 rounded-none !border-none flex flex-col w-full max-w-full min-w-0">
                              <CardHeader>
                                <div className="flex flex-row items-center justify-between gap-4">
                                  <div className="flex flex-row items-center gap-2 filter-container">
                                    <Input
                                      id="search-input"
                                      data-testid="search-input"
                                      className="text-sm font-normal border-none !w-[200px] xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                      placeholder={`Search by ${filterType === "Name"
                                          ? "Name"
                                          : "OS Type"
                                        }`}
                                      value={searchQuery}
                                      onChange={(e) => handleSearchChange(e)}
                                      prefix={<SearchIcon strokeWidth={2} />}
                                      size="regular"
                                      autoComplete="off"
                                    />
                                    <Select
                                      key={`filter-select-${filterType}`}
                                      height={1}
                                      className="text-sm font-normal font-w-400 !border-none !outline-none filter-by-vapp !w-[200px] xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                      id="filter-tempName"
                                      data-testid="filter-tempName"
                                      onChange={(e) =>
                                        handleFilterTypeChange(e)
                                      }
                                      placeholder={
                                        filterType === "Name"
                                          ? "Name"
                                          : "OS Type"
                                      }
                                      value={filterType}
                                      size="regular"
                                      defaultValue={filterType}
                                    >
                                      <SelectOption
                                        label={
                                          CREATE_COURSE_CONSTANTS
                                            .VAPP_TEMPLATE_DETAILS.FILTER
                                            .OPTIONS.TEMPLATE_NAME
                                        }
                                        value={"Name"}
                                        className="text-sm font-normal xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                      />
                                      <SelectOption
                                        label={
                                          CREATE_COURSE_CONSTANTS
                                            .VAPP_TEMPLATE_DETAILS.FILTER
                                            .OPTIONS.OS_TYPE
                                        }
                                        value={"OS Type"}
                                        className="text-sm font-normal xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                      />
                                    </Select>
                                  </div>
                                  <div className="flex items-center gap-2 text-sm flex-shrink-0 xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[20px]">
                                    {VIEW_COURSE_CONSTANTS.resultsRange(
                                      displayRows.length > 0 ? 1 : 0,
                                      displayRows.length,
                                      tableData.length
                                    )}
                                    <span className="mr-2 ml-2">|</span>
                                    {filterType === "Name"
                                      ? "Filter Applied by Name"
                                      : filterType === "OS Type"
                                        ? "Filter Applied by OS Type"
                                        : "Filter Applied by Name"}
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent
                                x="none"
                                y="none"
                                className="overflow-auto vm-table-height w-full min-w-0"
                              >
                                <Table
                                  fixedFirstColumn
                                  onCard
                                  className="w-full min-w-0"
                                  data-testid="vm-table"
                                >
                                  <TableHeader
                                    data={vmTableHeaders}
                                    variant="default"
                                    canResize
                                    onSort={handleTableSort}
                                  />
                                  {displayRows.length === 0 ? (
                                    <TableBody>
                                      <TableRow data-testid="no-data-row">
                                        <TableCell
                                          colSpan={vmTableHeaders.length}
                                          className="text-center text-sm font-bold vpp-table-border xl-low:!text-[12px] xl-mid:!text-[18px] xl-high:!text-[20px]"
                                        >
                                          {
                                            COURSE_INSTANCE_CONSTANTS.NO_DATA_FOUND
                                          }
                                        </TableCell>
                                      </TableRow>
                                    </TableBody>
                                  ) : (
                                    <TableContent
                                      data={displayRows.map((row, i) => ({
                                        id: `table-${i}`,
                                        cell: [
                                          {
                                            content: (
                                              <div
                                                onClick={() =>
                                                  row && row.vmURL && getMksToken(row)
                                                }
                                                className={`${row && row.vmURL ? "cursor-pointer" : ""} text-inherit hover:none truncate whitespace-nowrap block`}
                                              >
                                                <TextOverflow>
                                                  {row.vmName || "-"}
                                                </TextOverflow>
                                              </div>
                                            ),
                                            className: `vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap ${row && row.vmURL ? "light-blue" : ""}`,
                                          },
                                          {
                                            content: <TextOverflow>{row.osType || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.osVersionName || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.ramValue || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.cpu || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.iPv4 || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.storage || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                          {
                                            content: <TextOverflow>{row.isPausable || "-"}</TextOverflow>,
                                            className: "vpp-table-border !text-sm xl-low:!text-[14px] xl-mid:!text-[18px] xl-high:!text-[24px] truncate whitespace-nowrap",
                                          },
                                        ],
                                      }))}
                                      variant="compact"
                                    />
                                  )}
                                </Table>
                              </CardContent>
                            </Card>
                          </div>
                        </AccordionItem>
                      </Accordion>
                    )}
                    <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-0 pb-4 pr-6">
                      <Button
                        id="cancel-button"
                        data-testid="cancel-button"
                        title="cancel"
                        type="button"
                        onClick={() => {
                          setIsCancelButtonTriggered(true);
                          setShowCancelConfirmation(true);
                        }}
                        variant="quiet-secondary"
                        className="sm:mr-4 instance-cancel-button !text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px] xl-high:!h-12 xl-high:!py-3"
                      >
                        {COURSE_INSTANCE_CONSTANTS.CANCEL}
                      </Button>
                      <Button
                        id="next-button"
                        data-testid="next-button"
                        title="next"
                        type="submit"
                        variant="primary"
                        className="!text-sm xl-low:!text-[14px] xl-mid:!text-[21px] xl-high:!text-[24px] xl-high:!h-12 xl-high:!py-3"
                      >
                        {COURSE_INSTANCE_CONSTANTS.NEXT}
                      </Button>
                    </div>
                  </Card>
                </form>
              )}
              {!showGeneralDetails && (
                <ParticipantDetails
                  setShowGeneralDetails={setShowGeneralDetails}
                  updateSteps={updateSteps}
                  savedStudents={savedStudents}
                  savedInstructors={savedInstructors}
                  saveStudents={saveStudents}
                  saveInstructors={saveInstructors}
                  generalDetailsData={formData}
                  instanceId={instanceId}
                  selectedCourse={selectedCourse}
                  scrollToTop={() => {
                    if (scrollContainerRef.current) {
                      scrollContainerRef.current.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {showCancelConfirmation && (
        <Modal
          backdrop
          className="cancel-confirmation-modal border border-[#E0E0E0] border-[0.2px]"
          footerLeft={
            <div className="flex justify-center">
              <Button
                className="cancel-modal-btns mr-4 w-full"
                id="confirm-cancel"
                data-testid="confirm-cancel"
                onClick={() => setShowCancelConfirmation(false)}
                size="regular"
                variant="secondary"
              >
                {instanceId
                  ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION
                    .BUTTONS.NO
                  : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION
                    .BUTTONS.NO}
              </Button>
              <Button
                className="cancel-modal-btns w-full"
                id="confirm-continue"
                data-testid="confirm-continue"
                onClick={handleContinueWithoutSaving}
                size="regular"
                variant="primary"
              >
                {instanceId
                  ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION
                    .BUTTONS.YES
                  : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION
                    .BUTTONS.YES}
              </Button>
            </div>
          }
          onModalClose={() => setShowCancelConfirmation(false)}
          title={
            instanceId
              ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION.TITLE
              : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION.TITLE
          }
        >
          <div className="xl-low:!text-sm xl-mid:!text-base xl-high:!text-lg">
            {instanceId
              ? COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_UPDATION
                .MESSAGE
              : COURSE_INSTANCE_CONSTANTS.CANCEL_COURSE_INSTANCE_CREATION
                .MESSAGE}
          </div>
        </Modal>
      )}
    </>
  );
}
